/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { onIe } from '../../language/browser';
import { BLACK, COOL_GREY, DARK_GREY, DEFERRED_ORANGE, RED_CREEK, WHITE } from '../../styles/colors';
import { OVERFLOW_MENU_CTA_HEIGHT_PX } from './GuestCardOverflowMenu';
import { FADE_IN_ANIMATION } from '../../styles/animations';
import { LargeText, MediumText } from '../../styles/styles';


/**
 * The margin around the grid of guest cards
 */
export const CARD_GRID_MARGIN = 20;

/**
 * The padding on the inside edge of the grid of guest cards
 */
export const CARD_GRID_PADDING = 10;

export const MIN_CARD_WIDTH    = 280;
export const MAX_CARD_WIDTH    = 375;
export const CARD_HEIGHT       = 445;
export const CARD_MARGIN       = 10;
export const CARD_BORDER_WIDTH = 1;
export const CARD_PADDING      = 15;

export const CIRCULAR_RADIUS = '100%';
export const NO_RADIUS       = '0';

export const GUEST_CARD_BASE_STYLE = css`
  flex-basis: ${MIN_CARD_WIDTH}px;
  min-width: ${MIN_CARD_WIDTH}px;
  max-width: ${MAX_CARD_WIDTH}px;
  flex-grow: 1;
  overflow: hidden;
`;

export const GuestCardRoot = css`
  ${GUEST_CARD_BASE_STYLE};
  position: relative; /* Relative so overflow menu's absolute has something to position against */
  height: ${CARD_HEIGHT}px;
  background-color: ${WHITE};
  border: solid ${COOL_GREY} ${CARD_BORDER_WIDTH}px;
  box-shadow: ${DARK_GREY};
  ${FADE_IN_ANIMATION};
`;

/**
 * A div styled to take up the same amount of space as a normal GuestCard but without all the content.
 */
export const Blank = styled('div')`
  ${GUEST_CARD_BASE_STYLE};
  height: 1px; /* Height doesn't matter, it just has to take up horizontal space */
  border: solid transparent ${CARD_BORDER_WIDTH}px;
  opacity: 0;
`;

export const ellipsizeAtSecondLine = css`
  /* Webkit browsers only -- sorry IE */
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2; 
  display: -webkit-box;
  -webkit-box-orient: vertical;
  word-break: break-word;
  hyphens: auto;
  overflow-wrap: break-word;
`;

export const ellipsizeAtFirstLine = css`
  /* Webkit browsers only -- sorry IE */
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 1; 
  display: -webkit-box;
  -webkit-box-orient: vertical;
  word-break: break-word;
  hyphens: auto;
  overflow-wrap: break-word;
`;

export const ellipsizeAtFirstLineOnIe = css`
white-space: nowrap;
overflow: hidden;
text-overflow:ellipsis;
display: block;
`;

const nameStyle = css`
  max-width: 100%;
  max-height: 50%;
  margin-top: 5px;
  font-weight: bold;
  ${MediumText};
  ${ellipsizeAtSecondLine};
  
  @media(min-width: 1174px) {
    ${LargeText};
  }
`;

export const FirstName = styled('span')`
  ${nameStyle};
  margin-top: 0;
  color: ${BLACK};
  cursor: pointer;
`;

export const LastName = styled('span')`
  ${nameStyle};
  color: ${DARK_GREY};
  cursor: pointer;
`;

export const ErrorText = styled('span')`
  color: ${RED_CREEK};
  font-size: 1.6em;
  text-align: center;
`;


export const AgeText = styled('span')`
  ${MediumText}; 
  color: ${BLACK};
  margin-top: 5px;
`;

export const MgidText = styled('span')`
  ${MediumText};
  color: ${BLACK};
  margin-top: 5px;
  width: 100%;
  word-wrap: break-word;
`;

const OverflowMenuContainer = css`
  position: absolute;
  width: 100%;
  bottom: 0;
`;

export const ExpandedOverflowMenuContainer = css`
  ${OverflowMenuContainer};
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  transition: background-color .3s ease-in;
`;

export const CollapsedOverflowMenuContainer = css`
  ${OverflowMenuContainer};
  height: ${OVERFLOW_MENU_CTA_HEIGHT_PX}px;

  /*
  Animate background & delay height change for other animations to run while there's height
  enough to show them.
  */
  transition: background-color .3s ease-out, height .3s ease .3s;
`;

/**
 * The click-to-close empty space above the overflow menu when expanded -- this is its div's style.
 */
export const OverflowMenuEmptySpace = css`
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 0;
  cursor: pointer;
`;

export const OverflowMenuExpanded = css`
  width: 100%;
`;

export const OverflowMenuCollapsed = css`
  width: 100%;
  height: ${OVERFLOW_MENU_CTA_HEIGHT_PX}px; /* Only animates with set value */
`;

export const HotListBanner = styled('div')`
  height: 17px;
  padding: 3px 0;
  background-color: ${(props: {deferred: boolean, disabled: boolean}) => 
          props.deferred ? DEFERRED_ORANGE : props.disabled ? DARK_GREY : RED_CREEK};
  transition: all 1s ease;
  letter-spacing: 2px;
  font-size: 18px;
  padding-left: 1.5em;
  padding-right: 1.5em;
`;

export const HotListBannerText = styled('div')`
  text-transform: uppercase;
  color: ${WHITE};
  margin: 0;
  letter-spacing: 2.2px;
  text-align: center;
  ${onIe() ? ellipsizeAtFirstLineOnIe : ellipsizeAtFirstLine}
`;
